import {
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
  type EnvironmentProviders,
} from '@angular/core';

import { Action } from '../../../core/constants/action-catalogue';
import { ViewerIdentityService } from '../../../core/services/viewer-identity.service';
import { CommunityQuestionsService } from '../../../core/services/community-questions.service';
import { markHubsOnboarded } from '../onboarding/hub-onboarding';

/**
 * A VIEWER, WITHOUT A SESSION.
 *
 * `/preview/hubs` is guard-free and unauthenticated, so `ViewerIdentityService`
 * holds null and `actions()` is empty. That is correct — and it makes two
 * things unreviewable, because the composer reads permissions to decide what
 * to draw:
 *
 *   · the "Go live" card, gated on `live-event:manage | live-event:broadcast`;
 *   · the OWNED / stream-key segment inside it, gated on
 *     `live-event:broadcast` alone.
 *
 * Without a viewer the harness shows five cards and no hosting choice, which
 * is exactly what an ordinary employee sees — correct, and useless for
 * reviewing the path that only a broadcaster walks.
 *
 * ── IT SEEDS A BROADCASTER, AND SAYS SO ───────────────────────────────────
 * The fixture holds the two live-event rights and NOTHING else beyond a name,
 * so the harness shows the fullest version of the composer. It does not
 * pretend to be a real permission model: the real one is the ActionGuard, and
 * every gate here is re-checked server-side. What this proves is that the
 * client draws the right controls when the actions are present — not that the
 * actions would ever be granted.
 *
 * ── AND IT ANSWERS THE FIRST-VISIT SHEET ──────────────────────────────────
 * `im-hub-onboarding` opens whenever `im-hubs-onboarded` is unset, and every
 * Playwright run is a fresh browser context — so without this the harness
 * opens behind a modal and every drive assertion past the first fails on an
 * element it cannot reach. That is a harness fact, not a product one.
 *
 * The sheet is still reviewable: `?onboarding=1` forces it open regardless,
 * which is also how it gets screenshotted after the first run of the day.
 */
/**
 * ⚠ A LOCAL IDENTITY, NOT THE APP'S — AND THAT IS THE WHOLE POINT.
 *
 * This used to let the initializer below `inject(ViewerIdentityService)`,
 * which resolves the ROOT instance because the service is `providedIn:
 * 'root'`. The initializer then MUTATED it, so opening `/preview/hubs` once
 * repointed the running session at an invented person called "You" — and the
 * home page greeted the signed-in user as "Good morning, You." on the dev
 * instance. Reported from a real login, 1 Sep 2026.
 *
 * `{ preview: true }` did not save it. That flag makes `ensureLoaded()` fall
 * through to a real fetch instead of trusting the stub, which repairs the
 * session EVENTUALLY — but the greeting reads `viewerName()` straight off the
 * signal, and until some later code path happens to call `ensureLoaded()` the
 * signal still says "You". A marker on a value that should never have been
 * written globally is a smaller fix than not writing it.
 *
 * So the route gets its OWN `ViewerIdentityService`. The initializer runs in
 * this route's environment injector, so its `inject()` finds this instance,
 * and every component under the route resolves the same one. Nothing reaches
 * the shell. This is exactly what `content.routes.ts` does for
 * `/preview/content`, and for the same reason — that harness needs a viewer
 * holding grants nobody should be given, and it must not leak either.
 *
 * ⚠ DECLARED BEFORE the initializer. Providers are resolved in order, and an
 * initializer registered ahead of the override would capture the root
 * instance again and the leak would come straight back.
 */
export const HUBS_PREVIEW_VIEWER_PROVIDER: EnvironmentProviders =
  makeEnvironmentProviders([
    {
      provide: ViewerIdentityService,
      useFactory: (): ViewerIdentityService => new ViewerIdentityService(),
    },
    provideEnvironmentInitializer(() => {
      inject(ViewerIdentityService).setViewer(
        {
          _id: 'preview-viewer',
          email: 'you@ricoh.com',
          name: 'You',
          firstName: 'You',
          lastName: '',
          customerId: 'preview-customer',
          // ARTICLE_UPDATE_ANY is the reader's analytics grant
          // (READER_ANALYTICS_MANAGE_ACTION): the harness stands in for a comms
          // administrator so the Analytics control is reachable with no
          // session, and hubs-surface.spec.ts presses it. It gates nothing
          // else on these surfaces.
          // `event:create` is a default employee action; seeded so the hub's
          // "New event" is reviewable, as it is for anyone signed in.
          // ARTICLE_CREATE (articles-v2): the harness viewer writes articles,
          // so the composer's Article card is reviewable and leads into the
          // writer's own harness.
          actions: ['live-event:manage', 'live-event:broadcast', Action.ARTICLE_UPDATE_ANY, Action.ARTICLE_CREATE, 'event:create'],
        },
        // Still marked. It is no longer load-bearing for the leak — the
        // instance is local now — but `isPreview` is also how `ensureLoaded`
        // tells a stub from a real viewer, and a harness identity that claims
        // to be real would be a second bug wearing the first one's clothes.
        { preview: true },
      );
      markHubsOnboarded();
      // ── AND THE QUESTION RECORDS ──────────────────────────────────────
      // A question tile carries a pointer; the answers are read by id, and
      // that read 401s here. `openInViewer` prefers a seeded row, so seeding
      // is what lets the harness open a question and see its answers at all.
      // ⚠ LAZY, LIKE EVERY OTHER READ OF THIS MODULE. `app.routes.ts` imports
      // this provider as a VALUE, so a top-level import of the fixtures puts
      // 84 kB of prototype content in the eager `main.js` for every visitor on
      // every route. `inject` still happens synchronously, inside the
      // initializer where it is legal; only the corpus is deferred.
      //
      // The seed lands a microtask later than it used to, which is orders of
      // magnitude before the first user action that could open a question.
      const questions = inject(CommunityQuestionsService);
      void import('./hubs-preview.fixtures').then(
        ({ HUBS_PREVIEW_QUESTIONS }) =>
          questions.seedPreview(HUBS_PREVIEW_QUESTIONS),
      );
    }),
  ]);
